import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, TextField, Button, Stack,
  InputAdornment, CircularProgress, Divider, MenuItem,
} from '@mui/material';
import { Close, ArrowUpward, ArrowDownward, SwapVert } from '@mui/icons-material';
import { useAddCashMovementMutation, useLazyGetSessionQuery, useGetCashMovementReasonsQuery } from '../../services/cashSessionApi';
import { useDispatch } from 'react-redux';
import { setCurrentSession } from '../../store/cashSessionSlice';
import { useToast } from '../common/ToastProvider';

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: '#F8FAFC',
    borderRadius: '8px',
    fontSize: '0.9rem',
    '& fieldset': { borderColor: '#E2E8F0' },
    '&:hover fieldset': { borderColor: '#CBD5E1' },
    '&.Mui-focused fieldset': { borderColor: '#4F46E5' },
  },
  '& .MuiInputLabel-root': { fontSize: '0.875rem' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#4F46E5' },
};

const CashMovementModal = ({ open, onClose, session }) => {
  const dispatch  = useDispatch();
  const showToast = useToast();

  const [movementType, setMovementType] = useState('cash_in');
  const [amount, setAmount]             = useState('');
  const [reason, setReason]             = useState(''); // the comment
  const [reasonId, setReasonId]         = useState('');
  const [amountError, setAmountError]   = useState('');
  const [reasonError, setReasonError]   = useState('');
  const [reasonIdError, setReasonIdError] = useState('');

  // The org's reasons for this movement type. If they can't be loaded (older
  // backend, network blip) the modal falls back to the old free-text reason,
  // which the backend files under "Other" — the cashier is never blocked.
  // Loaded once for both types and filtered here, so switching Cash In / Out
  // is instant and never shows the other type's reasons.
  const { data: reasonsData, isFetching: reasonsLoading, isError: reasonsError } =
    useGetCashMovementReasonsQuery(undefined, { skip: !open });
  const reasons = (reasonsData?.data?.cash_movement_reasons || [])
    .filter((r) => r.applies_to === 'both' || r.applies_to === movementType);
  const useReasonList = !reasonsError && reasons.length > 0;
  const selectedReason = reasons.find((r) => String(r.id) === String(reasonId));
  const commentRequired = useReasonList ? !!selectedReason?.comment_required : true;

  const [addCashMovement, { isLoading: isSubmitting }] = useAddCashMovementMutation();
  const [triggerGetSession] = useLazyGetSessionQuery();

  // Reset form each time modal opens
  useEffect(() => {
    if (open) {
      setMovementType('cash_in');
      setAmount('');
      setReason('');
      setReasonId('');
      setAmountError('');
      setReasonError('');
      setReasonIdError('');
    }
  }, [open]);

  // A Cash Out reason means nothing for a Cash In — pick again after switching.
  const selectType = (type) => {
    if (type === movementType) return;
    setMovementType(type);
    setReasonId('');
    setReasonIdError('');
    setReasonError('');
  };

  const validate = () => {
    let valid = true;

    const val = parseFloat(amount);
    if (amount === '' || isNaN(val)) {
      setAmountError('Amount is required');
      valid = false;
    } else if (val <= 0) {
      setAmountError('Amount must be greater than 0');
      valid = false;
    } else {
      setAmountError('');
    }

    if (useReasonList && !reasonId) {
      setReasonIdError('Pick a reason');
      valid = false;
    } else {
      setReasonIdError('');
    }

    if (commentRequired && !reason.trim()) {
      setReasonError(useReasonList ? `A comment is required for "${selectedReason?.name || 'this reason'}"` : 'Reason is required');
      valid = false;
    } else {
      setReasonError('');
    }

    return valid;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      await addCashMovement({
        cash_session_id: session.id,
        movement_type:   movementType,
        amount:          parseFloat(amount),
        reason:          reason.trim() || undefined,
        ...(useReasonList ? { cash_movement_reason_id: Number(reasonId) } : {}),
      }).unwrap();

      // Refresh session in Redux so summary stays current
      try {
        const res = await triggerGetSession(session.id).unwrap();
        if (res?.data?.session) {
          dispatch(setCurrentSession(res.data.session));
        }
      } catch {
        // Non-critical — session summary will update next time modal opens
      }

      const label = movementType === 'cash_in' ? 'Cash in' : 'Cash out';
      showToast(`${label} of ₹${parseFloat(amount).toFixed(2)} recorded`, 'success');
      onClose();
    } catch (err) {
      const msg = err?.data?.error || 'Failed to record movement. Please try again.';
      showToast(msg, 'error');
    }
  };

  const handleAmountChange = (e) => {
    const val = e.target.value;
    if (/^\d*\.?\d{0,2}$/.test(val)) {
      setAmount(val);
      if (amountError) setAmountError('');
    }
  };

  const isCashIn  = movementType === 'cash_in';
  const isCashOut = movementType === 'cash_out';

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: '12px' } }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          px: 2.5, py: 2,
          display: 'flex', alignItems: 'center', gap: 1.5,
          borderBottom: '1px solid #F1F5F9',
        }}
      >
        <Box
          sx={{
            width: 34, height: 34, borderRadius: '9px',
            bgcolor: '#F0FDF4', display: 'flex',
            alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}
        >
          <SwapVert sx={{ fontSize: 19, color: '#16A34A' }} />
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          <Typography fontWeight={800} fontSize="1rem" color="#1E293B" lineHeight={1.3}>
            Cash Movement
          </Typography>
          {session?.session_number && (
            <Typography fontSize="0.7rem" color="#94A3B8" fontWeight={500} noWrap>
              {session.session_number}
            </Typography>
          )}
        </Box>
        <Button
          size="small"
          onClick={onClose}
          disabled={isSubmitting}
          sx={{ minWidth: 0, p: 0.5, color: '#94A3B8', '&:hover': { bgcolor: '#F1F5F9' } }}
        >
          <Close fontSize="small" />
        </Button>
      </DialogTitle>

      <DialogContent sx={{ px: 2.5, pt: 2.5, pb: 1 }}>

        {/* Movement type toggle */}
        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#64748B', mb: 1 }}>
          Movement Type
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mb: 2.5 }}>

          {/* Cash In */}
          <Button
            fullWidth
            variant={isCashIn ? 'contained' : 'outlined'}
            onClick={() => selectType('cash_in')}
            disabled={isSubmitting}
            disableElevation
            startIcon={<ArrowUpward sx={{ fontSize: 16 }} />}
            sx={{
              py: 1,
              fontSize: '0.82rem',
              fontWeight: 700,
              borderRadius: '8px',
              textTransform: 'none',
              ...(isCashIn
                ? {
                    bgcolor: '#16A34A',
                    color: '#fff',
                    '&:hover': { bgcolor: '#15803D' },
                  }
                : {
                    borderColor: '#D1FAE5',
                    color: '#16A34A',
                    bgcolor: '#F0FDF4',
                    '&:hover': { borderColor: '#16A34A', bgcolor: '#DCFCE7' },
                  }),
            }}
          >
            Cash In
          </Button>

          {/* Cash Out */}
          <Button
            fullWidth
            variant={isCashOut ? 'contained' : 'outlined'}
            onClick={() => selectType('cash_out')}
            disabled={isSubmitting}
            disableElevation
            startIcon={<ArrowDownward sx={{ fontSize: 16 }} />}
            sx={{
              py: 1,
              fontSize: '0.82rem',
              fontWeight: 700,
              borderRadius: '8px',
              textTransform: 'none',
              ...(isCashOut
                ? {
                    bgcolor: '#EF4444',
                    color: '#fff',
                    '&:hover': { bgcolor: '#DC2626' },
                  }
                : {
                    borderColor: '#FEE2E2',
                    color: '#EF4444',
                    bgcolor: '#FFF5F5',
                    '&:hover': { borderColor: '#EF4444', bgcolor: '#FEE2E2' },
                  }),
            }}
          >
            Cash Out
          </Button>

        </Stack>

        <Divider sx={{ mb: 2.5 }} />

        {/* Amount */}
        <TextField
          fullWidth
          label="Amount"
          required
          value={amount}
          onChange={handleAmountChange}
          disabled={isSubmitting}
          error={!!amountError}
          helperText={amountError || ' '}
          inputProps={{ inputMode: 'decimal' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Typography sx={{ fontWeight: 700, color: '#64748B', fontSize: '0.95rem' }}>₹</Typography>
              </InputAdornment>
            ),
          }}
          sx={{
            ...fieldSx,
            mb: 0.5,
            // Dynamic border colour based on movement type when focused
            '& .MuiOutlinedInput-root.Mui-focused fieldset': {
              borderColor: isCashIn ? '#16A34A' : '#EF4444',
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: isCashIn ? '#16A34A' : '#EF4444',
            },
          }}
        />

        {/* Reason — dropdown of the org's reasons for this movement type */}
        {useReasonList && (
          <TextField
            select
            fullWidth
            label="Reason"
            required
            value={reasonId}
            onChange={(e) => { setReasonId(e.target.value); setReasonIdError(''); setReasonError(''); }}
            disabled={isSubmitting}
            error={!!reasonIdError}
            helperText={reasonIdError || ' '}
            SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 320 } } } }}
            sx={{ ...fieldSx, mb: 0.5 }}
          >
            {reasons.map((r) => (
              <MenuItem key={r.id} value={String(r.id)} sx={{ fontSize: '0.88rem' }}>
                {r.name}
                {r.comment_required && (
                  <Typography component="span" sx={{ fontSize: '0.7rem', color: '#94A3B8', ml: 1 }}>comment needed</Typography>
                )}
              </MenuItem>
            ))}
          </TextField>
        )}
        {!useReasonList && reasonsLoading && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, color: '#94A3B8' }}>
            <CircularProgress size={14} sx={{ color: '#94A3B8' }} />
            <Typography sx={{ fontSize: '0.78rem' }}>Loading reasons…</Typography>
          </Stack>
        )}

        {/* Comment (the old free-text reason when no list is available) */}
        <TextField
          fullWidth
          label={useReasonList ? 'Comment' : 'Reason'}
          required={commentRequired}
          multiline={useReasonList}
          minRows={useReasonList ? 2 : undefined}
          inputProps={{ maxLength: 500 }}
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
            if (reasonError) setReasonError('');
          }}
          disabled={isSubmitting}
          error={!!reasonError}
          helperText={reasonError || ' '}
          placeholder={useReasonList
            ? (commentRequired ? 'Required — add the details' : 'Optional — e.g. bank name, bill no., person')
            : (isCashIn ? 'e.g. Petty cash replenishment' : 'e.g. Safe drop')}
          sx={{ ...fieldSx, mb: 0.5 }}
        />

      </DialogContent>

      <DialogActions sx={{ px: 2.5, pt: 1, pb: 2.5, gap: 1 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={isSubmitting}
          sx={{
            flex: 1, py: 1.1, fontWeight: 600, fontSize: '0.85rem',
            borderRadius: '8px', textTransform: 'none',
            borderColor: '#E2E8F0', color: '#64748B',
            '&:hover': { borderColor: '#CBD5E1', bgcolor: '#F8FAFC' },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting || !amount || (useReasonList ? !reasonId : !reason.trim()) || (commentRequired && !reason.trim())}
          disableElevation
          sx={{
            flex: 2, py: 1.1, fontWeight: 700, fontSize: '0.85rem',
            borderRadius: '8px', textTransform: 'none',
            bgcolor: isCashIn ? '#16A34A' : '#EF4444',
            color: '#fff',
            '&:hover': { bgcolor: isCashIn ? '#15803D' : '#DC2626' },
            '&.Mui-disabled': { bgcolor: '#E2E8F0', color: '#94A3B8' },
          }}
        >
          {isSubmitting ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={15} sx={{ color: '#fff' }} />
              <span>Recording...</span>
            </Stack>
          ) : (
            `Record ${isCashIn ? 'Cash In' : 'Cash Out'}`
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CashMovementModal;
